import React, { useContext, useEffect, useReducer, useState } from "react";

import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit } from "@material-ui/icons";
import ApiKeyModal from "../../components/ApiKeyModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_APIKEYS":
      const newKeys = [];
      action.payload.forEach((key) => {
        const index = state.findIndex((k) => k.id === key.id);
        if (index !== -1) state[index] = key;
        else newKeys.push(key);
      });
      return [...state, ...newKeys];
    case "UPDATE_APIKEYS":
      const updatedKey = action.payload;
      const index = state.findIndex((k) => k.id === updatedKey.id);
      if (index !== -1) {
        state[index] = updatedKey;
        return [...state];
      }
      return [updatedKey, ...state];
    case "DELETE_APIKEY":
      return state.filter((k) => k.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const ApiKeys = () => {
  const classes = useStyles();
  const [apiKeys, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const history = useHistory();
  const companyId = user.companyId;
  const socketManager = useContext(SocketContext);

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useOpenAi) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => history.push("/"), 1000);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await getApiKeys();
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const socket = socketManager.getSocket(companyId);
    socket.on(`company-${companyId}-apiKey`, (data) => {
      if (["update", "create"].includes(data.action)) {
        dispatch({ type: "UPDATE_APIKEYS", payload: data.apiKey });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_APIKEY", payload: data.apiKeyId });
      }
    });
    return () => socket.disconnect();
  }, [companyId, socketManager]);

  const getApiKeys = async () => {
    const { data } = await api.get("/apikey");
    dispatch({ type: "LOAD_APIKEYS", payload: data.apiKeys });
  };

  const handleOpenApiKeyModal = () => {
    setApiKeyModalOpen(true);
    setSelectedApiKey(null);
  };

  const handleCloseApiKeyModal = () => {
    setApiKeyModalOpen(false);
    setSelectedApiKey(null);
  };

  const handleEditApiKey = (apiKey) => {
    setSelectedApiKey(apiKey);
    setApiKeyModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedApiKey(null);
  };

  const handleDeleteApiKey = async (apiKeyId) => {
    try {
      const { data } = await api.delete(`/apikey/${apiKeyId}`);
      dispatch({ type: "DELETE_APIKEY", payload: apiKeyId });
      toast.info(i18n.t(data.message));
    } catch (err) {
      toastError(err);
    }
    setSelectedApiKey(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedApiKey &&
          `${i18n.t("apiKey.confirmationModal.deleteTitle")} ${selectedApiKey.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteApiKey(selectedApiKey.id)}
      >
        {i18n.t("apiKey.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <ApiKeyModal
        open={apiKeyModalOpen}
        onClose={handleCloseApiKeyModal}
        apiKeyId={selectedApiKey?.id}
        refreshApiKeys={getApiKeys}
      />

      <MainHeader>
        <Title>{i18n.t("apiKey.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" onClick={handleOpenApiKeyModal}>
            {i18n.t("apiKey.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="left">{i18n.t("apiKey.table.name")}</TableCell>
              <TableCell align="left">{i18n.t("apiKey.table.key")}</TableCell>
              <TableCell align="left">{i18n.t("apiKey.table.type")}</TableCell>
              <TableCell align="center">{i18n.t("apiKey.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {apiKeys.map((apiKey) => (
                <TableRow key={apiKey.id}>
                  <TableCell align="left">{apiKey.name}</TableCell>
                  <TableCell align="left">{apiKey.key}</TableCell>
                  <TableCell align="left">{apiKey.type}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEditApiKey(apiKey)}>
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedApiKey(apiKey);
                        setConfirmModalOpen(true);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default ApiKeys;
